// staff.js - director-side Coaches & Staff screen (Phase 0c Fix 3).
// One staff record per real person; spelling variants ("Sara" / "Sarah
// McAuley") arrive as separate records seeded from historical data and the
// director merges them here. Merging re-points the alias map, so every
// evaluation and selection record written under any spelling counts toward
// the canonical person from then on. Module 6 (Coach Hub) builds on the same
// records.

import { CONFIG } from './config.js';
import { DB } from './db.js';
import { DTA_BADGES, badgeSrc, badgeLabel } from './dta-badges.js';
import { DTA_AGE_TAGS } from './dta-data.js';
import { STAFF_TYPES, staffType, filterStaffByType, staffTypeCounts } from './staff-data.js';
import { STAFF_EVAL } from './staff-eval.js';
import { STAFF_NOTES } from './staff-notes.js';
import { t } from './strings.js';
import { UI } from './ui.js';
import { UTILS } from './utils.js';

export const STAFF = (() => {

  let _staff = [];        // [{ id, full_name, staff_aliases: [{alias_norm}], ... }]
  let _evalCounts = {};   // alias_norm -> number of evaluations
  let _trainerCounts = {}; // alias_norm -> number of selection_trainer records
  let _codes = {};        // staff_id -> active staff_codes row (Coach Hub login)
  let _teams = [];        // all teams (for the assignment picker)
  let _typeFilter = '';   // '' = all, else coach | trainer | both | none
  let _plans = [];        // all Season Plans
  let _assignments = [];  // staff_team_assignments rows
  let _evals = [];        // this season's open staff_evaluations

  async function show() {
    UI.showScreen('screen-staff-manager');
    UI.setLoading('staff-content', t('common.loading'));
    try {
      const [staffRows, evalNames, trainers, codeRows, teams, plans, assigns, evals] = await Promise.all([
        DB.staff.list(),
        DB.staff.evalCoachNames().catch(() => []),
        DB.selection.listTrainers().catch(() => []),
        DB.staffHub.listCodes().catch(() => []),
        DB.teams.getAll().catch(() => []),
        DB.evaluationPlans.list().catch(() => []),
        DB.staffHub.listAssignments().catch(() => []),
        // Open evaluations this season, so each row can show whether that
        // person has been asked for a self-evaluation and whether they replied.
        DB.staffHub.listEvaluations().catch(() => []),
      ]);
      _evals = (evals || []).filter(e =>
        e.season_id === CONFIG.season.current && !e.locked_at);
      _staff = staffRows || [];
      _teams = teams || [];
      _plans = plans || [];
      _assignments = assigns || [];
      _codes = {};
      (codeRows || []).forEach(c => {
        if (!c.revoked_at) _codes[c.staff_id] = c;
      });

      _evalCounts = {};
      (evalNames || []).forEach(e => {
        const k = UTILS.normName(e.coach_name);
        if (!k || k === 'director') return;
        _evalCounts[k] = (_evalCounts[k] || 0) + 1;
      });

      _trainerCounts = {};
      (trainers || []).forEach(tr => {
        const k = UTILS.normName(tr.name);
        if (!k) return;
        _trainerCounts[k] = (_trainerCounts[k] || 0) + 1;
      });

      _render();
    } catch (err) {
      console.error('Staff list failed:', err);
      UI.setEmpty('staff-content', t('common.error'));
    }
  }

  function _countsFor(s) {
    const aliases = (s.staff_aliases || []).map(a => a.alias_norm);
    return {
      aliases: aliases.length,
      evals:   aliases.reduce((n, a) => n + (_evalCounts[a] || 0), 0),
    };
  }

  function _render() {
    const el = document.getElementById('staff-content');
    if (!el) return;
    const esc = UTILS.escapeHtml;

    const counts = staffTypeCounts(_staff);
    const shown = filterStaffByType(_staff, _typeFilter);
    const typeChip = (key, label, n) => `
      <button class="sel-section-toggle ${_typeFilter === key ? 'sel-section-toggle--on' : ''}"
              data-staff-type="${esc(key)}">${esc(label)} (${n})</button>`;

    el.innerHTML = `
      <div class="sel-detail-actions" style="margin-bottom:10px;">
        <button class="btn btn--primary" id="staff-add">${esc(t('staff.add'))}</button>
        ${_staff.length ? `<button class="btn btn--secondary" id="staff-request-self">${esc(t('staff.requestSelf'))}</button>` : ''}
      </div>
      <div class="sel-section-toggles" style="margin-bottom:10px;">
        ${typeChip('', t('staff.typeAll'), counts.all)}
        ${STAFF_TYPES.map(x => typeChip(x.key, t(x.labelKey), counts[x.key])).join('')}
      </div>
      <div class="sel-detail-hint" style="margin-bottom:12px;">${esc(t('staff.hint'))}</div>
      ${!_staff.length ? `<div class="empty-state"><p class="empty-state__message">${esc(t('staff.empty'))}</p></div>` : ''}
      ${_staff.length && !shown.length ? `<div class="empty-state"><p class="empty-state__message">${esc(t('staff.noneOfType'))}</p></div>` : ''}
      ${shown.map(s => {
        const c = _countsFor(s);
        const hasCode = !!_codes[s.id];
        const ty = staffType(s);
        return `
          <div class="sel-code-row" style="flex-wrap:wrap;">
            <div style="flex:1 1 100%; min-width:0;">
              <div class="sel-code-row__name">${esc(s.full_name)}
                <span class="dta-badge ${ty === 'none' ? 'dta-badge--draft' : 'dta-badge--trainer'}">${esc(t('staff.type_' + ty))}</span>
                ${hasCode ? `<span class="player-read-chip">${esc(t('shub.codeActive'))}</span>` : ''}
                ${/* The switches now live behind one Access button, so the row
                      has to say what is on without being opened. */ ''}
                ${s.is_evaluator ? `<span class="dta-badge dta-badge--pub">${esc(t('staff.evaluatorBadge'))}</span>` : ''}
                ${s.unrestricted_eval ? `<span class="dta-badge dta-badge--pub">${esc(t('staff.anytimeBadge'))}</span>` : ''}
                ${s.season_plan_access ? `<span class="dta-badge dta-badge--trainer">${esc(t('staff.plansBadge'))}</span>` : ''}
                ${s.oversees_ages ? `<span class="dta-badge dta-badge--pub">${esc(t('staff.overseesBadge', { ages: (s.trainer_ages || []).join(', ') }))}</span>` : ''}
                ${_selfEvalChip(s.id, esc)}</div>
              <div class="staff-row-counts">
                ${esc(t('staff.aliasCount', { count: c.aliases }))} ·
                ${esc(t('staff.evalCount', { count: c.evals }))}
              </div>
            </div>
            <div style="display:flex; gap:6px; flex-wrap:wrap;">
              <button class="sel-roster-btn" data-staff-teams="${esc(s.id)}">${esc(t('shub.teams', { count: _assignments.filter(a => a.staff_id === s.id).length }))}</button>
              <button class="sel-roster-btn" data-staff-evals="${esc(s.id)}">${esc(t('shub.evaluate'))}</button>
              <button class="sel-roster-btn" data-staff-notes="${esc(s.id)}">${esc(t('shub.notes'))}</button>
              ${hasCode
                ? `<button class="sel-roster-btn" data-staff-code-show="${esc(s.id)}">${esc(t('shub.codeShow'))}</button>
                   <button class="sel-roster-btn sel-roster-btn--danger" data-staff-code-revoke="${esc(s.id)}">${esc(t('shub.codeRevoke'))}</button>`
                : `<button class="sel-roster-btn" data-staff-code-issue="${esc(s.id)}">${esc(t('shub.codeIssue'))}</button>`}
              <button class="sel-roster-btn" data-staff-role="${esc(s.id)}">${esc(t('staff.access'))}</button>
              <button class="sel-roster-btn" data-staff-rename="${esc(s.id)}">${esc(t('staff.rename'))}</button>
              <button class="sel-roster-btn" data-staff-merge="${esc(s.id)}">${esc(t('staff.merge'))}</button>
              <button class="sel-roster-btn sel-roster-btn--danger" data-staff-archive="${esc(s.id)}">${esc(t('staff.archive'))}</button>
            </div>
          </div>`;
      }).join('')}
    `;

    document.getElementById('staff-add')?.addEventListener('click', _addPerson);
    document.getElementById('staff-request-self')?.addEventListener('click', _showRequestSelf);
    el.querySelectorAll('[data-staff-rename]').forEach(b =>
      b.addEventListener('click', () => _rename(b.dataset.staffRename)));
    el.querySelectorAll('[data-staff-merge]').forEach(b =>
      b.addEventListener('click', () => _showMergeModal(b.dataset.staffMerge)));
    el.querySelectorAll('[data-staff-archive]').forEach(b =>
      b.addEventListener('click', () => _archive(b.dataset.staffArchive)));
    el.querySelectorAll('[data-staff-teams]').forEach(b =>
      b.addEventListener('click', () => _showTeamsModal(b.dataset.staffTeams)));
    el.querySelectorAll('[data-staff-evals]').forEach(b =>
      b.addEventListener('click', () => {
        const s = _staff.find(x => x.id === b.dataset.staffEvals);
        if (s) STAFF_EVAL.showList(s, show);
      }));
    el.querySelectorAll('[data-staff-notes]').forEach(b =>
      b.addEventListener('click', () => {
        const s = _staff.find(x => x.id === b.dataset.staffNotes);
        if (s) STAFF_NOTES.show(s, show);
      }));
    el.querySelectorAll('[data-staff-code-issue]').forEach(b =>
      b.addEventListener('click', () => _issueCode(b.dataset.staffCodeIssue)));
    el.querySelectorAll('[data-staff-code-show]').forEach(b =>
      b.addEventListener('click', () => _showCode(b.dataset.staffCodeShow)));
    el.querySelectorAll('[data-staff-code-revoke]').forEach(b =>
      b.addEventListener('click', () => _revokeCode(b.dataset.staffCodeRevoke)));
    el.querySelectorAll('[data-staff-role]').forEach(b =>
      b.addEventListener('click', () => {
        const s = _staff.find(x => x.id === b.dataset.staffRole);
        if (s) _showAccessModal(s);
      }));
    el.querySelectorAll('[data-staff-type]').forEach(b =>
      b.addEventListener('click', () => {
        const v = b.dataset.staffType;
        _typeFilter = _typeFilter === v ? '' : (v || '');
        _render();
      }));
  }

  // Role dialog: Coach and/or Trainer, plus the age groups a trainer's DTA
  // content is scoped to. One write covers both halves. Role changes are baked
  // into the JWT at login, so they take effect at their next sign-in.
  /**
   * Everything one person is allowed to reach, on one screen.
   *
   * These switches used to live in three places: what they are in a Role dialog,
   * and three separate buttons on an already crowded roster row. Read together
   * they are one decision about one person, so they are now made together, and
   * the same panel is offered at the moment you approve someone rather than only
   * afterwards.
   *
   * Every switch here is enforced in the database except "evaluates anytime",
   * which is a workflow gate rather than a fence. It is on this panel anyway,
   * because it is a decision the director makes about a person, and hiding it
   * somewhere else would not make it any more true.
   */
  function _showAccessModal(s) {
    const esc = UTILS.escapeHtml;
    let isCoach      = !!s.is_coach;
    let isTrainer    = !!s.is_trainer;
    let isEvaluator  = !!s.is_evaluator;
    let anytime      = !!s.unrestricted_eval;
    let seesPlans    = !!s.season_plan_access;
    let overseesAges = !!s.oversees_ages;
    const ages = new Set(s.trainer_ages || []);
    let badge = s.dta_badge || null;

    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    const render = () => {
      // Written on one line on purpose. .modal__body is white-space: pre-line,
      // so every newline in this markup would render as a real blank line.
      const sw = (key, on, label, hint) =>
        `<button type="button" class="access-switch ${on ? 'access-switch--on' : ''}" data-ac-sw="${key}">`
        + `<span class="access-switch__mark">${on ? '&#10003;' : ''}</span>${esc(label)}`
        + `<span class="access-switch__hint">${esc(hint)}</span></button>`;

      overlay.innerHTML = `
        <div class="modal" style="max-width:440px; max-height:90vh; overflow-y:auto;">
          <div class="modal__title">${esc(t('staff.accessTitle', { name: s.full_name }))}</div>
          <div class="modal__body">
            <div class="gate__label">${esc(t('staff.accessWhatThey'))}</div>
            <div class="sel-section-toggles">
              <button type="button" class="sel-section-toggle ${isCoach ? 'sel-section-toggle--on' : ''}" data-rl="coach">${esc(t('staff.typeCoach'))}</button>
              <button type="button" class="sel-section-toggle ${isTrainer ? 'sel-section-toggle--on' : ''}" data-rl="trainer">${esc(t('staff.typeTrainer'))}</button>
            </div>
            <p class="sel-detail-hint" style="margin:8px 0 0;">${esc(t('staff.roleHint'))}</p>
            ${isTrainer ? `
              <div class="gate__label" style="margin-top:12px;">${esc(t('staffAccess.trainerAges'))}</div>
              <p class="sel-detail-hint" style="margin:0 0 6px;">${esc(t('shub.trainerHint'))}</p>
              <div class="sel-section-toggles">
                ${DTA_AGE_TAGS.map(a => `<button type="button" class="sel-section-toggle ${ages.has(a) ? 'sel-section-toggle--on' : ''}" data-rl-age="${esc(a)}">${esc(a)}</button>`).join('')}
              </div>` : ''}

            <div class="gate__label" style="margin-top:16px;">${esc(t('staff.accessCanReach'))}</div>
            <div class="access-switches">
              ${sw('plans',     seesPlans,   t('staff.swPlans'),     t('staff.swPlansHint'))}
              ${sw('anytime',   anytime,     t('staff.swAnytime'),   t('staff.swAnytimeHint'))}
              ${sw('evaluator', isEvaluator, t('staff.swEvaluator'), t('staff.swEvaluatorHint'))}
              ${/* Only meaningful with trainer ages ticked, since those ages ARE
                    the scope. Shown greyed rather than hidden so the director can
                    see the switch exists and why it is not available yet. */ ''}
              ${isTrainer && ages.size
                ? sw('overseesAges', overseesAges, t('staff.swOversees'),
                     t('staff.swOverseesHint', { ages: [...ages].join(', ') }))
                : `<div class="access-switch" style="opacity:.5; cursor:default;">`
                  + `<span class="access-switch__mark"></span>${esc(t('staff.swOversees'))}`
                  + `<span class="access-switch__hint">${esc(t('staff.swOverseesNeedsAges'))}</span></div>`}
            </div>
            <p class="sel-detail-hint" style="margin:10px 0 0;">${esc(t('staff.accessTiming'))}</p>

            ${/* The badge is recognition, not permission, so it sits below the
                  switches with its own heading rather than among them. Nothing
                  here grants or removes access. */ ''}
            <div class="gate__label" style="margin-top:16px;">${esc(t('staff.badgeTitle'))}</div>
            <p class="sel-detail-hint" style="margin:0 0 8px;">${esc(t('staff.badgeHint'))}</p>
            <div class="sel-section-toggles">
              <button class="sel-section-toggle ${!badge ? 'sel-section-toggle--on' : ''}"
                      data-ac-badge="">${esc(t('staff.badgeNone'))}</button>
              ${DTA_BADGES.map(b => `
                <button class="sel-section-toggle ${badge === b.key ? 'sel-section-toggle--on' : ''}"
                        data-ac-badge="${esc(b.key)}">${esc(b.label)}</button>`).join('')}
            </div>
            ${badge ? `
              <div class="staff-badge-preview">
                <img src="${esc(badgeSrc(badge))}" alt="" width="56" height="56">
                <span>${esc(t('staff.badgePreview', { name: badgeLabel(badge) }))}</span>
              </div>` : ''}
          </div>
          <div class="modal__buttons">
            <button class="modal-btn" data-rl-action="cancel">${esc(t('common.cancel'))}</button>
            <button class="modal-btn modal-btn--primary" data-rl-action="save">${esc(t('common.save'))}</button>
          </div>
        </div>`;

      overlay.querySelectorAll('[data-rl]').forEach(b =>
        b.addEventListener('click', () => {
          if (b.dataset.rl === 'coach') isCoach = !isCoach; else isTrainer = !isTrainer;
          render();   // re-render so the age picker appears/disappears with Trainer
        }));
      overlay.querySelectorAll('[data-rl-age]').forEach(b =>
        b.addEventListener('click', () => {
          const a = b.dataset.rlAge;
          if (ages.has(a)) { ages.delete(a); b.classList.remove('sel-section-toggle--on'); }
          else { ages.add(a); b.classList.add('sel-section-toggle--on'); }
        }));
      overlay.querySelectorAll('[data-ac-badge]').forEach(b =>
        b.addEventListener('click', () => {
          badge = b.dataset.acBadge || null;   // '' is the "no badge" chip
          render();                            // re-render to move the tick + preview
        }));
      overlay.querySelectorAll('[data-ac-sw]').forEach(b =>
        b.addEventListener('click', () => {
          const k = b.dataset.acSw;
          if (k === 'plans') seesPlans = !seesPlans;
          if (k === 'anytime') anytime = !anytime;
          if (k === 'evaluator') isEvaluator = !isEvaluator;
          if (k === 'overseesAges') overseesAges = !overseesAges;
          render();
        }));
      overlay.querySelector('[data-rl-action="cancel"]').addEventListener('click', () => overlay.remove());
      overlay.querySelector('[data-rl-action="save"]').addEventListener('click', async () => {
        const list = isTrainer ? [...ages] : [];
        const btn = overlay.querySelector('[data-rl-action="save"]');
        btn.disabled = true;
        try {
          // Only what actually moved. Four writes for a panel where the director
          // changed one switch would be three needless round trips, and three
          // more chances for a half-applied save.
          const roleChanged = isCoach !== !!s.is_coach || isTrainer !== !!s.is_trainer
            || String(list) !== String(s.trainer_ages || []);
          if (roleChanged) await DB.staff.setRole(s.id, { isCoach, isTrainer, trainerAges: list });
          if (isEvaluator !== !!s.is_evaluator)      await DB.staff.setEvaluator(s.id, isEvaluator);
          if (anytime     !== !!s.unrestricted_eval) await DB.staff.setUnrestricted(s.id, anytime);
          if (seesPlans   !== !!s.season_plan_access) await DB.staff.setSeesPlans(s.id, seesPlans);
          // Ages are the scope, so losing them has to lose the switch too.
          // Otherwise someone stays flagged as overseeing an empty list, which
          // reads as access but grants nothing and is impossible to debug.
          const keepsOversight = overseesAges && isTrainer && list.length > 0;
          if (keepsOversight !== !!s.oversees_ages) await DB.staff.setOverseesAges(s.id, keepsOversight);
          if ((badge || null) !== (s.dta_badge || null)) await DB.staff.setDtaBadge(s.id, badge);

          s.is_coach = isCoach; s.is_trainer = isTrainer; s.trainer_ages = list;
          s.is_evaluator = isEvaluator; s.unrestricted_eval = anytime;
          s.season_plan_access = seesPlans;
          s.oversees_ages = keepsOversight;
          s.dta_badge = badge;
          UI.toast(t('staff.accessSaved', { name: s.full_name }), 'success', 2000);
          overlay.remove();
          _render();
        } catch (err) {
          console.error('Access save failed:', err);
          btn.disabled = false;
          UI.toast(t('common.error'), 'error');
        }
      });
    };
    document.body.appendChild(overlay);
    render();
  }

  // ── Team assignments (which teams a coach evaluates + the plan) ──────────────

  function _showTeamsModal(staffId) {
    const s = _staff.find(x => x.id === staffId);
    if (!s) return;
    const esc = UTILS.escapeHtml;

    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';

    const render = () => {
      const mine = _assignments.filter(a => a.staff_id === staffId);
      const assignedIds = new Set(mine.map(a => a.team_id));
      // One plan governs this coach's rotation - take it from their first
      // assignment (kept in sync across all their teams below).
      const currentPlanId = mine.find(a => a.plan_id)?.plan_id || '';

      overlay.innerHTML = `
        <div class="modal" style="max-width:460px; max-height:88vh; overflow-y:auto;">
          <div class="modal__title">${esc(t('shub.teamsTitle', { name: s.full_name }))}</div>
          <div class="modal__body">
            <div class="sel-detail-hint" style="margin-bottom:8px;">${esc(t('shub.teamsHint'))}</div>
            <div class="gate__label">${esc(t('shub.teamsPlan'))}</div>
            <select class="field" id="staff-teams-plan">
              <option value="">${esc(t('shub.teamsNoPlan'))}</option>
              ${_plans.map(p => `<option value="${esc(p.id)}" ${p.id === currentPlanId ? 'selected' : ''}>${esc(p.name)}</option>`).join('')}
            </select>
            <div class="gate__label" style="margin-top:12px;">${esc(t('shub.teamsPick'))}</div>
            <div class="sel-section-toggles">
              ${_teams.map(tm => `
                <button class="sel-section-toggle ${assignedIds.has(tm.id) ? 'sel-section-toggle--on' : ''}" data-assign-team="${esc(tm.id)}">${esc(tm.id)}</button>`).join('')}
            </div>
          </div>
          <div class="modal__buttons">
            <button class="modal-btn modal-btn--primary" data-action="done">${esc(t('common.save') || 'Done')}</button>
          </div>
        </div>`;

      overlay.querySelectorAll('[data-assign-team]').forEach(btn =>
        btn.addEventListener('click', async () => {
          const teamId = btn.dataset.assignTeam;
          const planId = overlay.querySelector('#staff-teams-plan')?.value || null;
          try {
            if (assignedIds.has(teamId)) {
              await DB.staffHub.unassignTeam(staffId, teamId);
              _assignments = _assignments.filter(a => !(a.staff_id === staffId && a.team_id === teamId));
            } else {
              await DB.staffHub.assignTeam(staffId, teamId, planId);
              _assignments.push({ staff_id: staffId, team_id: teamId, plan_id: planId });
            }
            render();
          } catch (err) {
            console.error('Assign team failed:', err);
            UI.toast(t('common.error'), 'error');
          }
        }));

      // Changing the plan re-stamps every current assignment for this coach.
      overlay.querySelector('#staff-teams-plan')?.addEventListener('change', async (e) => {
        const planId = e.target.value || null;
        const mineNow = _assignments.filter(a => a.staff_id === staffId);
        try {
          for (const a of mineNow) {
            await DB.staffHub.assignTeam(staffId, a.team_id, planId);
            a.plan_id = planId;
          }
        } catch (err) {
          console.error('Plan re-stamp failed:', err);
          UI.toast(t('common.error'), 'error');
        }
      });

      overlay.querySelector('[data-action="done"]')
        .addEventListener('click', () => { overlay.remove(); _render(); });
    };

    document.body.appendChild(overlay);
    render();
  }

  // ── Coach Hub login codes ────────────────────────────────────────────────────

  async function _issueCode(staffId) {
    const s = _staff.find(x => x.id === staffId);
    if (!s) return;
    // ST- prefix routes the gate to staff-auth; no-lookalike alphabet so the
    // code survives WhatsApp / paper.
    const code = UTILS.generateCode('ST', {
      length: 10, alphabet: 'ABCDEFGHJKMNPQRSTUVWXYZ23456789',
    });
    try {
      await DB.staffHub.createCode(staffId, code);
      _codes[staffId] = { staff_id: staffId, code };
      UI.modal({
        title: t('shub.codeIssuedTitle', { name: s.full_name }),
        body:  `${code}\n\n${t('shub.codeIssuedBody')}`,
        buttons: [
          { label: t('shub.codeCopy'), style: 'primary', action: async () => {
            try { await navigator.clipboard.writeText(code); UI.toast(t('shub.codeCopied'), 'success', 1200); }
            catch { /* code stays visible in the row below */ }
            _render();
          } },
          { label: 'OK', action: _render },
        ],
      });
    } catch (err) {
      console.error('Issue staff code failed:', err);
      UI.toast(t('common.error'), 'error');
    }
  }

  // Re-show an existing code so the director can read it out or copy it to
  // re-send - no need to revoke + re-issue when a coach loses their code.
  function _showCode(staffId) {
    const s = _staff.find(x => x.id === staffId);
    const code = _codes[staffId]?.code;
    if (!s || !code) return;
    UI.modal({
      title: t('shub.codeIssuedTitle', { name: s.full_name }),
      body:  `${code}\n\n${t('shub.codeIssuedBody')}`,
      buttons: [
        { label: t('shub.codeCopy'), style: 'primary', action: async () => {
          try { await navigator.clipboard.writeText(code); UI.toast(t('shub.codeCopied'), 'success', 1200); }
          catch { /* code stays visible in the modal */ }
        } },
        { label: 'OK' },
      ],
    });
  }

  function _revokeCode(staffId) {
    const s = _staff.find(x => x.id === staffId);
    const codeRow = _codes[staffId];
    if (!s || !codeRow) return;
    UI.modal({
      title: t('shub.codeRevokeTitle', { name: s.full_name }),
      body:  t('shub.codeRevokeBody'),
      buttons: [
        { label: t('shub.codeRevoke'), style: 'primary', action: async () => {
          try {
            // The row id may be missing on a just-issued local entry - refetch.
            const rows = await DB.staffHub.listCodes();
            const live = (rows || []).find(c => c.staff_id === staffId && !c.revoked_at);
            if (live) await DB.staffHub.revokeCode(live.id);
            delete _codes[staffId];
            UI.toast(t('shub.codeRevoked'), 'success', 1200);
            _render();
          } catch (err) {
            console.error('Revoke staff code failed:', err);
            UI.toast(t('common.error'), 'error');
          }
        } },
        { label: 'Cancel' },
      ],
    });
  }

  async function _addPerson() {
    const name = (await UI.promptTextAsync({
      title: t('staff.addTitle'),
      label: t('staff.addLabel'),
      singleLine: true,
    }) || '').trim();
    if (!name) return;

    // One record per real person - block names that already exist (as a
    // display name or as any known spelling variant).
    const norm = UTILS.normName(name);
    const exists = _staff.some(s =>
      UTILS.normName(s.full_name) === norm ||
      (s.staff_aliases || []).some(a => a.alias_norm === norm));
    if (exists) {
      UI.toast(t('staff.addExists'), 'info');
      return;
    }

    try {
      await DB.staff.create(name);
      UI.toast(t('staff.added', { name }), 'success', 1500);
      show();
    } catch (err) {
      console.error('Staff add failed:', err);
      UI.toast(t('common.error'), 'error');
    }
  }

  // ── Self-evaluation requests ────────────────────────────────────────────────
  // Asking is deliberate. A coach is never prompted for a self-evaluation until
  // the director asks, and the ask is per person, per season.

  /** This person's open evaluation for the current season, if any. */
  function _openEval(staffId) {
    return _evals.find(e => e.staff_id === staffId) || null;
  }

  /** Where a requested self-evaluation has got to: asked, or answered. */
  function _selfEvalChip(staffId, esc) {
    const e = _openEval(staffId);
    if (!e?.reflection_requested_at) return '';
    const answered = !!(e.reflection_well || e.reflection_improve || e.reflection_support);
    // Green once their answers are in, amber while still waiting: the two read
    // as one scale, which muted text next to a badge did not.
    return answered
      ? `<span class="dta-badge dta-badge--pub">${esc(t('staff.selfDone'))}</span>`
      : `<span class="dta-badge dta-badge--draft">${esc(t('staff.selfAsked'))}</span>`;
  }

  /**
   * Pick who to ask.
   *
   * Built for a full roster, not a handful: fifty names as a wall of chips is
   * unreadable, so this is a searchable, role-filtered list of rows, each
   * showing where that person already stands. Anyone already asked is
   * pre-ticked, so the dialog reads as current state rather than a blank form.
   */
  function _showRequestSelf() {
    const esc = UTILS.escapeHtml;
    const chosen = new Set(_staff.filter(s => _openEval(s.id)?.reflection_requested_at).map(s => s.id));
    let q = '', typeF = '';

    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    document.body.appendChild(overlay);
    const close = () => overlay.remove();

    const visible = () => {
      const needle = q.trim().toLowerCase();
      return filterStaffByType(_staff, typeF)
        .filter(s => !needle || s.full_name.toLowerCase().includes(needle));
    };

    const paint = () => {
      const rows = visible();
      const counts = staffTypeCounts(_staff);
      const chip = (key, label, n) => `
        <button type="button" class="sel-section-toggle ${typeF === key ? 'sel-section-toggle--on' : ''}"
                data-rs-type="${esc(key)}">${esc(label)} (${n})</button>`;

      overlay.innerHTML = `
        <div class="modal rs-modal">
          <div class="modal__title">${esc(t('staff.requestSelfTitle'))}</div>
          <div class="modal__body">
            <p class="sel-detail-hint" style="margin:0 0 10px;">${esc(t('staff.requestSelfHint'))}</p>
            <input class="field" id="rs-search" type="search"
                   placeholder="${esc(t('staff.requestSelfSearch'))}" value="${esc(q)}" style="margin-bottom:8px;">
            <div class="sel-section-toggles" style="margin-bottom:8px;">
              ${chip('', t('staff.typeAll'), counts.all)}
              ${STAFF_TYPES.map(x => chip(x.key, t(x.labelKey), counts[x.key])).join('')}
            </div>
            <div class="rs-bulk">
              <button type="button" class="sel-roster-btn" data-rs-all>${esc(t('staff.requestSelfAll', { count: rows.length }))}</button>
              <button type="button" class="sel-roster-btn" data-rs-none>${esc(t('staff.requestSelfNone'))}</button>
            </div>
            <div class="rs-list">
              ${rows.length ? rows.map(s => {
                const e = _openEval(s.id);
                const answered = !!(e?.reflection_well || e?.reflection_improve || e?.reflection_support);
                const state = answered ? `<span class="dta-badge dta-badge--pub">${esc(t('staff.selfDone'))}</span>`
                  : e?.reflection_requested_at ? `<span class="dta-badge dta-badge--draft">${esc(t('staff.selfAsked'))}</span>` : '';
                return `
                  <label class="rs-row">
                    <input type="checkbox" class="acc-tick" data-rs="${esc(s.id)}" ${chosen.has(s.id) ? 'checked' : ''}>
                    <span class="rs-row__name">${esc(s.full_name)}</span>
                    <span class="dta-badge dta-badge--trainer">${esc(t('staff.type_' + staffType(s)))}</span>
                    ${state}
                  </label>`;
              }).join('') : `<div class="sel-detail-hint">${esc(t('dta.noResults'))}</div>`}
            </div>
          </div>
          <div class="modal__buttons">
            <button class="modal-btn" data-rs-action="cancel">${esc(t('common.cancel'))}</button>
            <button class="modal-btn modal-btn--primary" data-rs-action="send">${esc(t('staff.requestSelfSend', { count: chosen.size }))}</button>
          </div>
        </div>`;
      wire();
    };

    const wire = () => {
      overlay.querySelectorAll('[data-rs]').forEach(b =>
        b.addEventListener('change', () => {
          if (b.checked) chosen.add(b.dataset.rs); else chosen.delete(b.dataset.rs);
          // Repaint only the send button, so ticking never moves the list
          // under the finger of someone working down fifty rows.
          const send = overlay.querySelector('[data-rs-action="send"]');
          if (send) send.textContent = t('staff.requestSelfSend', { count: chosen.size });
        }));
      overlay.querySelectorAll('[data-rs-type]').forEach(b =>
        b.addEventListener('click', () => { typeF = b.dataset.rsType; paint(); }));
      overlay.querySelector('[data-rs-all]')?.addEventListener('click', () => {
        visible().forEach(s => chosen.add(s.id)); paint();
      });
      overlay.querySelector('[data-rs-none]')?.addEventListener('click', () => {
        visible().forEach(s => chosen.delete(s.id)); paint();
      });
      const search = overlay.querySelector('#rs-search');
      search?.addEventListener('input', (e) => {
        q = e.target.value;
        const pos = e.target.selectionStart;
        paint();
        const next = overlay.querySelector('#rs-search');
        if (next) { next.focus(); try { next.setSelectionRange(pos, pos); } catch { /* type=search */ } }
      });
      overlay.querySelector('[data-rs-action="cancel"]').addEventListener('click', close);
      overlay.querySelector('[data-rs-action="send"]').addEventListener('click', send);
    };

    async function send() {
      const btn = overlay.querySelector('[data-rs-action="send"]');
      btn.disabled = true;
      try {
        // Anyone un-ticked who was previously asked has the request withdrawn,
        // so this dialog is the single place that decides who is being asked.
        const withdraw = _staff
          .filter(s => !chosen.has(s.id) && _openEval(s.id)?.reflection_requested_at)
          .map(s => _openEval(s.id).id);
        for (const evalId of withdraw) await DB.staffHub.cancelSelfEval(evalId);
        const res = await DB.staffHub.requestSelfEval([...chosen], CONFIG.season.current);
        UI.toast(res.asked
          ? t('staff.requestSelfSent', { count: res.asked })
          : t('staff.requestSelfCleared'), 'success', 2000);
        close();
        show();
      } catch (err) {
        console.error('Self-evaluation request failed:', err);
        btn.disabled = false;
        UI.toast(t('common.error'), 'error');
      }
    }
  }

  async function _rename(id) {
    const s = _staff.find(x => x.id === id);
    if (!s) return;
    const name = (await UI.promptTextAsync({
      title: t('staff.renameTitle'),
      value: s.full_name,
      singleLine: true,
    }) || '').trim();
    if (!name || name === s.full_name) return;
    try {
      await DB.staff.rename(id, name);
      s.full_name = name;
      _render();
    } catch (err) {
      console.error('Staff rename failed:', err);
      UI.toast(t('common.error'), 'error');
    }
  }

  function _showMergeModal(fromId) {
    const from = _staff.find(x => x.id === fromId);
    if (!from) return;
    const esc = UTILS.escapeHtml;

    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.innerHTML = `
      <div class="modal" style="max-width:460px;">
        <div class="modal__title">${esc(t('staff.mergeTitle', { name: from.full_name }))}</div>
        <div class="modal__body">
          <input type="text" class="field prompt-input" id="staff-merge-search"
                 placeholder="${esc(t('sel.resolve.searchHint'))}">
          <div class="sel-resolve-list" id="staff-merge-list"></div>
        </div>
        <div class="modal__buttons">
          <button class="modal-btn" data-action="cancel">Cancel</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);

    const input  = overlay.querySelector('#staff-merge-search');
    const listEl = overlay.querySelector('#staff-merge-list');
    const close  = () => overlay.remove();

    const renderList = (q) => {
      const nq = UTILS.normName(q);
      const hits = _staff
        .filter(s => s.id !== fromId)
        .filter(s => !nq || UTILS.normName(s.full_name).includes(nq))
        .slice(0, 30);
      listEl.innerHTML = hits.map(s => `
        <button class="sel-resolve-row" data-into-id="${esc(s.id)}">
          <span>${esc(s.full_name)}</span>
        </button>`).join('');
      listEl.querySelectorAll('[data-into-id]').forEach(btn => {
        btn.addEventListener('click', () => {
          const into = _staff.find(x => x.id === btn.dataset.intoId);
          if (!into) return;
          close();
          UI.modal({
            title: t('staff.mergeTitle', { name: from.full_name }) + ' ' + into.full_name,
            body:  t('staff.mergeBody', { from: from.full_name, into: into.full_name }),
            buttons: [
              { label: t('staff.mergeConfirm'), style: 'primary', action: () => _merge(from, into) },
              { label: 'Cancel' },
            ],
          });
        });
      });
    };
    renderList('');
    input.addEventListener('input', () => renderList(input.value));
    input.focus();
    overlay.querySelector('[data-action="cancel"]').addEventListener('click', close);
  }

  async function _merge(from, into) {
    try {
      await DB.staff.merge(from.id, into.id);
      UI.toast(t('staff.merged', { name: into.full_name }), 'success');
      show(); // reload - alias counts changed
    } catch (err) {
      console.error('Staff merge failed:', err);
      UI.toast(t('common.error'), 'error');
    }
  }

  function _archive(id) {
    const s = _staff.find(x => x.id === id);
    if (!s) return;
    UI.modal({
      title: t('staff.archiveTitle', { name: s.full_name }),
      body:  t('staff.archiveBody'),
      buttons: [
        { label: t('staff.archive'), style: 'primary', action: async () => {
          try {
            await DB.staff.archive(id);
            UI.toast(t('staff.archived'), 'success', 1200);
            show();
          } catch (err) {
            console.error('Staff archive failed:', err);
            UI.toast(t('common.error'), 'error');
          }
        } },
        { label: 'Cancel' },
      ],
    });
  }

  return { show };

})();
